'use client';

import { c as _c } from "react/compiler-runtime";
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContentWrapper, DialogTitle, DialogTrigger } from '@/comps/primitives/dialog';
import { Input } from '@/comps/primitives/input';
import { Button } from '@/comps/primitives/button';
import { useCreateOrganization } from '@/api/generated/organizations/organizations';
import { useCreateProject } from '@/api/generated/projects/projects';
import { createOrganizationFormSchema, CreateOrganizationFormValues, ORG_PROJECT_NAME_MAX_LENGTH } from '@/domains/dashboard/components/create-organization-project.schema';
import { DEFAULT_CREATE_PROJECT_PRODUCT, ProjectProductSelector } from '@/domains/dashboard/components/project-product-selector';
import { NavElement } from '@/domains/home/components/home-tabs/_components/nav-button';
import { getFormFieldErrorMessage } from '@/domains/reports/schemas/report-identity.schema';
import { useProjectsContext } from '@/providers/projects-provider';
import { useForm } from '@tanstack/react-form';
import { Building2, Loader2 } from 'lucide-react';
export type CreateOrganizationResp = {
  organizationId: string;
  projectId: string;
};
export type CreateOrganizationModalProps = PropsWithChildren & {
  onCancel?: () => void;
  onConfirm?: (resp: CreateOrganizationResp) => void;
};
export function CreateOrganizationModal(t0) {
  const $ = _c(50);
  const {
    children,
    onCancel,
    onConfirm
  } = t0;
  const [open, setOpen] = useState(false);
  const {
    selectedProject
  } = useProjectsContext();
  const {
    mutateAsync: createProject,
    isLoading: isCreatingProject
  } = useCreateProject();
  const {
    mutateAsync: createOrg,
    isLoading: isCreatingOrg
  } = useCreateOrganization();
  const isCreating = isCreatingOrg || isCreatingProject;
  let t1;
  if ($[0] !== selectedProject?.product) {
    t1 = () => ({
      organizationName: "",
      projectName: "",
      product: selectedProject?.product ?? DEFAULT_CREATE_PROJECT_PRODUCT
    });
    $[0] = selectedProject?.product;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  selectedProject?.product;
  const getDefaultValues = t1;
  let t2;
  if ($[2] !== getDefaultValues) {
    t2 = getDefaultValues();
    $[2] = getDefaultValues;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      onSubmit: createOrganizationFormSchema
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const form = useForm({
    defaultValues: t2,
    validators: t3,
    onSubmit: async t4 => {
      const {
        value
      } = t4;
      if (isCreating) {
        return;
      }
      const orgResp = await createOrg({
        data: {
          name: value.organizationName.trim()
        }
      });
      const organizationId = orgResp.organization.id;
      if (!organizationId) {
        console.error("Error creating organization!");
        return;
      }
      const projectResp = await createProject({
        data: {
          name: value.projectName.trim(),
          organization_id: organizationId,
          product: value.product
        }
      });
      const projectId = projectResp.project.id;
      if (!projectId) {
        console.error("Error creating project!");
        return;
      }
      setOpen(false);
      form.reset(getDefaultValues());
      onConfirm?.({
        organizationId,
        projectId
      });
    }
  });
  let t5;
  if ($[5] !== form || $[6] !== getDefaultValues) {
    t5 = () => {
      form.reset(getDefaultValues());
    };
    $[5] = form;
    $[6] = getDefaultValues;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  form;
  const resetForm = t5;
  let t6;
  let t7;
  if ($[8] !== open || $[9] !== resetForm) {
    t6 = () => {
      if (open) {
        resetForm();
      }
    };
    t7 = [open, resetForm];
    $[8] = open;
    $[9] = resetForm;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== resetForm) {
    t8 = nextOpen => {
      setOpen(nextOpen);
      if (!nextOpen) {
        resetForm();
      }
    };
    $[12] = resetForm;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleOpenChange = t8;
  let t9;
  if ($[14] !== handleOpenChange || $[15] !== onCancel) {
    t9 = () => {
      onCancel?.();
      handleOpenChange(false);
    };
    $[14] = handleOpenChange;
    $[15] = onCancel;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleCancel = t9;
  let t10;
  if ($[17] !== children) {
    t10 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[17] = children;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DialogTitle><div className="flex items-center border-b px-6"><NavElement Icon={Building2} text="Create new organization" coloredIcon={true} isActive={true} showTooltip={false} /></div></DialogTitle>;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== form.Field) {
    t12 = <form.Field name="organizationName">{_temp}</form.Field>;
    $[20] = form.Field;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== form.Field) {
    t13 = <form.Field name="projectName">{_temp2}</form.Field>;
    $[22] = form.Field;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== form.Field) {
    t14 = <form.Field name="product">{_temp3}</form.Field>;
    $[24] = form.Field;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t12 || $[27] !== t13 || $[28] !== t14) {
    t15 = <div className="flex flex-col gap-4 px-6 py-4">{t12}{t13}{t14}</div>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== handleCancel || $[31] !== isCreating) {
    t16 = <Button onClick={handleCancel} variant="outline" disabled={isCreating}>Cancel</Button>;
    $[30] = handleCancel;
    $[31] = isCreating;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== form || $[34] !== isCreating) {
    t17 = isSubmitting => <Button disabled={isSubmitting || isCreating} onClick={() => void form.handleSubmit()} variant="default">{isCreating ? <><Loader2 className="mr-2 size-4 animate-spin" />Creating…</> : "Create"}</Button>;
    $[33] = form;
    $[34] = isCreating;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== form.Subscribe || $[37] !== t17) {
    t18 = <form.Subscribe selector={_temp4}>{t17}</form.Subscribe>;
    $[36] = form.Subscribe;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t16 || $[40] !== t18) {
    t19 = <div className="flex items-center justify-end gap-2 border-t px-6 py-3">{t16}{t18}</div>;
    $[39] = t16;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t15 || $[43] !== t19) {
    t20 = <DialogContentWrapper className="max-w-130 gap-0 p-0" container={document.body}><div className="flex flex-col">{t11}{t15}{t19}</div></DialogContentWrapper>;
    $[42] = t15;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== handleOpenChange || $[46] !== open || $[47] !== t10 || $[48] !== t20) {
    t21 = <Dialog open={open} onOpenChange={handleOpenChange}>{t10}{t20}</Dialog>;
    $[45] = handleOpenChange;
    $[46] = open;
    $[47] = t10;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  return t21;
}
function _temp4(state) {
  return state.isSubmitting;
}
function _temp3(field_1) {
  return <ProjectProductSelector value={field_1.state.value} onChange={field_1.handleChange} />;
}
function _temp2(field_0) {
  return <div className="flex flex-col gap-1.5"><label htmlFor="create-organization-modal--project-name" className="text-xs font-medium text-muted-foreground">Project name</label><Input id="create-organization-modal--project-name" value={field_0.state.value} onChange={e_0 => field_0.handleChange(e_0.currentTarget.value)} onBlur={field_0.handleBlur} placeholder="My new AI agent..." maxLength={ORG_PROJECT_NAME_MAX_LENGTH} invalid={field_0.state.meta.errors.length > 0} />{field_0.state.meta.errors.length > 0 && <p className="text-xs text-destructive">{getFormFieldErrorMessage(field_0.state.meta.errors)}</p>}</div>;
}
function _temp(field) {
  return <div className="flex flex-col gap-1.5"><label htmlFor="create-organization-modal--org-name" className="text-xs font-medium text-muted-foreground">Organization name</label><Input id="create-organization-modal--org-name" value={field.state.value} onChange={e => field.handleChange(e.currentTarget.value)} onBlur={field.handleBlur} placeholder="My organization..." maxLength={ORG_PROJECT_NAME_MAX_LENGTH} invalid={field.state.meta.errors.length > 0} />{field.state.meta.errors.length > 0 && <p className="text-xs text-destructive">{getFormFieldErrorMessage(field.state.meta.errors)}</p>}</div>;
}